import type { SignalDetail } from '../../../types/signals-live';
import { isAdminUser, type OrganizationDisplaySettings } from '../../../utils/api';

interface SignalActionSidebarProps {
  signal: SignalDetail;
  signalId?: string;
  user: { role?: string } | null | undefined;
  displaySettings: OrganizationDisplaySettings;
  isReclassifying: boolean;
  onEscalate: () => void;
  onDownloadReport: () => void;
  onTrack: () => void;
  onDismiss: () => void;
  onReclassify: () => void;
  onNavigateToTaxonomy: () => void;
}

export function SignalActionSidebar({
  signal,
  user,
  displaySettings,
  isReclassifying,
  onEscalate,
  onDownloadReport,
  onTrack,
  onDismiss,
  onReclassify,
  onNavigateToTaxonomy,
}: SignalActionSidebarProps) {
  return (
    <div className="w-96 overflow-y-auto rounded-lg bg-vanilla p-6 scrollbar-hide">
      <div className="mb-6">
        <h3 className="text-figma-lg mb-4 font-semibold text-figma-text-primary">Business Context</h3>
        <div className="rounded-lg bg-white p-4">
          <div className="space-y-4">
            <div>
              <p className="text-figma-xs mb-1 font-medium uppercase tracking-wide text-figma-text-secondary">Journey Stage</p>
              <p className="text-figma-sm text-figma-text-primary">{signal.context?.journey_stage || signal.journey_stage || 'Unknown'}</p>
            </div>
            <div>
              <p className="text-figma-xs mb-1 font-medium uppercase tracking-wide text-figma-text-secondary">Source</p>
              <p className="text-figma-sm text-figma-text-primary">{signal.source_type || signal.context?.channel || signal.channel || 'Unknown'}</p>
            </div>
            <div>
              <p className="text-figma-xs mb-1 font-medium uppercase tracking-wide text-figma-text-secondary">Product / Policy</p>
              <p className="text-figma-sm text-figma-text-primary">{signal.customer?.product_policy || signal.customer_product_policy || 'Not specified'}</p>
            </div>
          </div>
        </div>
      </div>

      {isAdminUser(undefined) &&
        displaySettings.show_costs &&
        signal.classification?.cost &&
        signal.classification.cost.total_cost !== null &&
        signal.classification.cost.total_cost !== undefined && (
          <div className="mb-6">
            <h3 className="text-figma-xs mb-3 font-medium uppercase tracking-wide text-figma-text-secondary">Classification Cost</h3>
            <div className="rounded-lg border border-figma-border bg-white p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-figma-sm text-figma-text-secondary">Total Cost:</span>
                <span className="text-figma-base font-semibold text-figma-text-primary">
                  ${signal.classification.cost.total_cost?.toFixed(4) || '0.0000'}
                </span>
              </div>
              {signal.classification.model_version && (
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-figma-sm text-figma-text-secondary">Model:</span>
                  <span className="text-figma-sm font-medium text-figma-text-primary">
                    {signal.classification.model_version}
                  </span>
                </div>
              )}
              <div className="text-figma-xs space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-figma-text-muted">Input tokens ({signal.classification.usage?.prompt_tokens?.toLocaleString() || 0}):</span>
                  <span className="text-figma-text-secondary">${signal.classification.cost.input_cost?.toFixed(4) || '0.0000'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-figma-text-muted">Output tokens ({signal.classification.usage?.completion_tokens?.toLocaleString() || 0}):</span>
                  <span className="text-figma-text-secondary">${signal.classification.cost.output_cost?.toFixed(4) || '0.0000'}</span>
                </div>
                <div className="mt-2 border-t border-figma-border pt-2">
                  <div className="flex items-center justify-between">
                    <span className="text-figma-text-muted">Total tokens:</span>
                    <span className="text-figma-text-secondary">{signal.classification.usage?.total_tokens?.toLocaleString() || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      {isAdminUser(user) &&
        displaySettings.show_langchain_details &&
        signal.classification &&
        (signal.classification.langsmith_run_id || signal.classification.langsmith_trace_id) && (
          <div className="mb-6">
            <h3 className="text-figma-xs mb-3 font-medium uppercase tracking-wide text-figma-text-secondary">LangSmith Metadata</h3>
            <div className="text-figma-xs space-y-2 rounded-lg border border-figma-border bg-white p-4">
              {signal.classification.langsmith_run_id && (
                <div className="flex items-center justify-between">
                  <span className="text-figma-text-muted">Run ID:</span>
                  <span className="break-all font-mono text-figma-text-secondary" title={signal.classification.langsmith_run_id}>
                    {signal.classification.langsmith_run_id}
                  </span>
                </div>
              )}
              {signal.classification.langsmith_trace_id && (
                <div className="flex items-center justify-between">
                  <span className="text-figma-text-muted">Trace ID:</span>
                  <span className="break-all font-mono text-figma-text-secondary" title={signal.classification.langsmith_trace_id}>
                    {signal.classification.langsmith_trace_id}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

      <div className="mb-6">
        <h3 className="text-figma-xs mb-3 font-medium uppercase tracking-wide text-figma-text-secondary">Notes</h3>
        <textarea
          className="text-figma-sm w-full resize-none rounded-lg border border-figma-border bg-white p-3 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-go-primary"
          rows={4}
          placeholder="Add notes about this signal..."
        />
      </div>

      <div className="space-y-3">
        <button
          onClick={onEscalate}
          className="text-figma-base w-full rounded-figma-button bg-go-primary py-3 font-medium text-white transition-colors hover:bg-sapphire"
        >
          Escalate as Complaint
        </button>
        <button
          onClick={onDownloadReport}
          className="text-figma-base w-full rounded-figma-button border border-figma-border bg-white py-3 font-medium text-figma-text-primary transition-colors hover:bg-figma-bg-vanilla"
        >
          Generate Audit Report
        </button>
        <button
          onClick={onNavigateToTaxonomy}
          className="text-figma-base w-full rounded-figma-button border border-indigo bg-white py-3 font-medium text-indigo transition-colors hover:bg-figma-bg-vanilla"
        >
          View in Taxonomy
        </button>
        <button
          onClick={onReclassify}
          disabled={isReclassifying}
          className={`text-figma-base flex w-full items-center justify-center rounded-figma-button border bg-white py-3 font-medium transition-colors ${isReclassifying ? 'cursor-not-allowed border-gray-300 text-gray-400' : 'border-purple text-purple hover:bg-figma-bg-vanilla'}`}
        >
          {isReclassifying ? (
            <>
              <svg className="-ml-1 mr-3 h-5 w-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Reclassifying...
            </>
          ) : (
            'Reclassify with AI'
          )}
        </button>
        <div className="flex space-x-3">
          <button
            onClick={onTrack}
            className="text-figma-base flex-1 rounded-figma-button border border-go-primary bg-white py-3 font-medium text-go-primary transition-colors hover:bg-figma-bg-vanilla"
          >
            Track
          </button>
          <button
            onClick={onDismiss}
            className="text-figma-base flex-1 rounded-figma-button border border-figma-border bg-white py-3 font-medium text-figma-text-secondary transition-colors hover:bg-figma-bg-vanilla"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}
